import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Utils from '../utils/Utils';

const NotificationsContext = createContext();

export const useNotifications = () => useContext(NotificationsContext);

export const NotificationsProvider = ({ children }) => {
  const isLoggedIn = useSelector(state => state.auth.isLoggedIn);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { serverURL } = Utils;

  const fetchNotifications = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${serverURL}/notifications`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const res = { data: await response.json() };
      setNotifications(
        res.data.map(n => ({
          id: n._id,
          text: n.title,
          createdAt: n.createdAt,
          read: n.isRead,
          type: n.type,
          metadata: n.metadata || {},
          navigateTo: n.metadata?.navigateTo || undefined,
        }))
      );
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, serverURL]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    // Listen for changes in notifications and force update
    // This will trigger a re-render in components using useNotifications
    console.log("Notifications updated:", notifications);
  }, [notifications]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await fetch(`${serverURL}/notifications/${notificationId}/read`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } catch (err) {
      setError(err);
    }
  }, [serverURL]);

  const addNotification = useCallback((n) => {
    setNotifications(prev => {
      // Prevent duplicate notifications by id
      if (prev.some(notif => notif.id === n._id)) return prev;
      return [
        {
          id: n._id,
          text: n.title,
          createdAt: n.createdAt,
          read: n.isRead,
          type: n.type,
          metadata: n.metadata || {},
          navigateTo: n.metadata?.navigateTo || undefined,
        },
        ...prev,
      ];
    });
  }, []);

  return (
    <NotificationsContext.Provider value={{
      notifications,
      loading,
      error,
      fetchNotifications,
      markAsRead,
      unreadCount: notifications.filter(n => !n.read).length,
      addNotification,
    }}>
      {children}
    </NotificationsContext.Provider>
  );
};
